import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Compass, Users, ListChecks } from "lucide-react";
import { useSEO, orgJsonLd } from "@/lib/seo";
import { Breadcrumbs, SectionLabel } from "@/components/common/states";

const VALUES = [
  { icon: Compass, title: "Curated, not cluttered", desc: "We focus on relevant options rather than overwhelming you with every listing." },
  { icon: ShieldCheck, title: "Transparent information", desc: "Clear, honest property details. Pricing and RERA are shared on enquiry." },
  { icon: ListChecks, title: "Requirement-first", desc: "Your budget, configuration and purpose drive every recommendation." },
  { icon: Users, title: "Advisor-led", desc: "Guidance from people who understand Pune's neighbourhoods." },
];

export default function About() {
  useSEO({ title: "About Endeavour Castle", description: "Endeavour Castle is a Pune-focused real-estate discovery and advisory platform helping buyers find the right residential and commercial property.", path: "/about", jsonLd: orgJsonLd });
  return (
    <div>
      <section className="relative py-24">
        <div className="absolute inset-0"><img src="https://images.pexels.com/photos/29285205/pexels-photo-29285205.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600" alt="Pune residential skyline" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-primary/85" /></div>
        <div className="relative ec-container">
          <div className="mb-4"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About" }]} /></div>
          <h1 className="font-display text-4xl sm:text-5xl text-white max-w-2xl">A considered approach to Pune real estate</h1>
          <p className="mt-4 text-white/85 text-lg max-w-2xl">Endeavour Castle helps you discover, compare and shortlist residential and commercial property across Pune — with clear information and real guidance.</p>
        </div>
      </section>

      <section className="ec-container py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionLabel>Our Approach</SectionLabel>
            <h2 className="font-display text-3xl text-foreground">What we do</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              We are a property discovery and advisory service focused exclusively on Pune. Our goal is simple: help you find the right property with less noise and more clarity.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              We work across Pune's key micro-markets — from the western IT corridor of Wakad, Baner and Hinjewadi to the eastern hubs of Kharadi and Viman Nagar, and premium pockets like Koregaon Park and Kalyani Nagar.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Property information on this platform includes clearly marked demo listings during development. Verified pricing, RERA and availability details are shared on enquiry.
            </p>
            <Link to="/requirements" className="mt-6 inline-flex items-center h-12 px-6 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">Tell Us Your Requirement</Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {VALUES.map((v) => (
              <div key={v.title} className="p-6 rounded-md border border-border bg-white">
                <div className="h-11 w-11 rounded-sm bg-primary/5 text-primary grid place-items-center mb-4"><v.icon className="h-5 w-5" aria-hidden /></div>
                <h3 className="font-display text-lg text-foreground mb-1.5">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
